/* eslint-disable no-unused-vars */
import alert from '@/plugins/alert'
import {CV, EnrollSlot, EnrollForm, StudentExam, Transaction} from '@/plugins/api'
import moment from 'moment'
import {getHighestEnglishScore, getHighestScore} from '../../plugins/resultUtils'

// Các mã ổn định của schema mới dùng để nhận diện hai luồng tuyển thẳng đang có.
// Không dùng checkpoint vì checkpoint là nội dung hiển thị và có thể thay đổi theo từng đợt tuyển sinh.
const DIRECT_ADMISSION_TYPES = ['direct_admission', 'internal_tqb']
const DIRECT_ADMISSION_METHOD_CODES = ['direct_admission', 'direct_admission_2']

const getCurrentCVFromState = state => {
  // Dùng cùng quy tắc với getter currentCV để selector hoạt động đúng cả ở màn người dùng và màn admin preview.
  return state.isAdminView ? state.tempCV : Object.values(state.CVs)[0]
}

const getPrimaryCVFromState = state => {
  // Ba getter cũ bên dưới vốn luôn đọc hồ sơ đầu tiên trong CVs.
  // Giữ riêng cách lấy này để việc đổi schema không vô tình thay đổi business hiện tại của các màn đang dùng getter cũ.
  return Object.values(state.CVs)[0]
}

// Một học sinh có thể có đơn nhập học ở nhiều khối (site c1/c2/c3) với cùng studentCode.
// Khi đang xem hồ sơ của chính học sinh đó thì chỉ lấy đơn thuộc hồ sơ này, tránh lấy nhầm đơn khối khác.
const getRefId = ref => (ref && typeof ref === 'object' ? ref.id || ref._id : ref)
const pickEnrollFormOfCV = (forms, cv, studentCode) => {
  if (!Array.isArray(forms) || !forms.length) return null
  if (!cv?.id || (studentCode && cv.studentCode !== studentCode)) return forms[0]
  if (!forms.some(form => form.cv)) return forms[0]
  return forms.find(form => String(getRefId(form.cv)) === String(cv.id)) || null
}

const getAdmissionResults = cv => {
  // Chuẩn hóa về mảng để các selector bên dưới không phải lặp lại kiểm tra null/undefined.
  return Array.isArray(cv?.admissionResult) ? cv.admissionResult : []
}

const getAllAdmissionProductResults = cv => {
  // Một học sinh có thể có nhiều kết quả theo phương thức/đợt xét tuyển.
  // Mỗi admissionResult lại chứa results[] của từng hệ, vì vậy cần gom phẳng trước khi tìm theo product.id.
  return getAdmissionResults(cv).reduce((productResults, admission) => {
    if (!Array.isArray(admission?.results)) return productResults
    return productResults.concat(admission.results)
  }, [])
}

const normalizeProductId = productId => {
  // ID từ API có thể là number hoặc string. Quy về string để so sánh ổn định nhưng không dùng name/acronym làm khóa.
  return productId === undefined || productId === null ? '' : String(productId)
}

// Selector nguồn của schema mới.
// Chỉ lấy kết quả của đúng hệ theo product.id; không có fallback sang tên hệ, acronym hoặc field schema cũ.
export const selectAdmissionProductResults = (cv, productId) => {
  const normalizedProductId = normalizeProductId(productId)
  if (!normalizedProductId) return []

  return getAllAdmissionProductResults(cv).filter(result => {
    return normalizeProductId(result?.product?.id) === normalizedProductId
  })
}

// Quy đổi kết quả của một product về status duy nhất.
// Thứ tự ưu tiên giữ theo business cũ: đạt chính thức > đạt có điều kiện > không đạt.
// `passCond` chỉ là shape đầu ra tạm thời cho component cũ; dữ liệu nguồn vẫn là status + conditional trong results[].
export const selectAdmissionProductStatus = (cv, productId) => {
  const productResults = selectAdmissionProductResults(cv, productId)

  if (productResults.some(result => result.status === 'pass' && result.conditional !== true)) return 'pass'
  if (productResults.some(result => result.status === 'pass' && result.conditional === true)) return 'passCond'
  if (productResults.some(result => result.status === 'fail')) return 'fail'
  return null
}

// Trả danh sách trạng thái theo đúng các hệ có trong results[].
// Danh sách giữ thứ tự xuất hiện từ API để giao diện không tự sắp xếp lại thứ tự hệ đào tạo.
// Nếu một hệ xuất hiện ở nhiều phương thức/đợt, status được tổng hợp theo cùng quy tắc ưu tiên của selector chung.
export const selectAdmissionProductStatuses = cv => {
  const productsById = new Map()

  getAllAdmissionProductResults(cv).forEach(result => {
    const productId = normalizeProductId(result?.product?.id)
    if (!productId || productsById.has(productId)) return

    productsById.set(productId, result.product)
  })

  return Array.from(productsById.entries()).map(([productId, product]) => ({
    productId,
    product,
    status: selectAdmissionProductStatus(cv, productId) || 'fail',
  }))
}

export default {
  namespaced: true,
  state: {
    CVs: [],
    count: 0,
    step: 1,
    systemTime: {
      checkSystemTime: {
        'open-document': false,
        'grade6-close-create': false,
        'grade10-close-create': false,
        'grade6-close-submit': false,
        'grade10-close-submit': false,
      },
      systemTime: {
        'open-document': moment().format('DD/MM/YYYY HH:mm:ss'),
      },
    },
    slot: null,
    // pending BIDV bill for the parent's reserved slot (set by fetchMySlot)
    pendingEnrollTransactionId: null,
    selectExpectation: null,
    enrollForm: null,
    query: null,
    tempCV: {},
    isAdminView: false,
    isEnrollFormScrolled: false,
    isAvatarChanged: false,
  },
  actions: {
    getDashboardCount(_, params) {
      return CV.countCVs(params)
    },
    getExcelExportData(_, payload) {
      return CV.exportExcel(payload)
    },
    async updateFormGrade10({commit}, data) {
      try {
        const result = await CV.updateFormGrade10(data)
        commit('setCV', result)
        alert.success('Cập nhật thành công!')
        return result
      } catch (e) {
        alert.error(e)
      }
    },
    async updateFormGrade1({commit}, data) {
      try {
        const result = await CV.updateFormGrade1(data)
        commit('setCV', result)
        alert.success('Cập nhật thành công!')
        return result
      } catch (e) {
        alert.error(e)
        throw e
      }
    },
    // Parent's own slot (reserved/paid) + pending BIDV bill id; replaces checkEnrollPaymentStatus (ZaloPay),
    // which returns slot = null for slots reserved through BIDV
    async fetchMySlot({commit}) {
      try {
        const {slot, pendingTransactionId} = (await Transaction.getMyEnrollSlot()) || {}
        commit('setSlot', {slot: slot || null})
        commit('changeState', {pendingEnrollTransactionId: pendingTransactionId || null})
        if (slot?.expectation) commit('setSelectExpectation', slot.expectation)
        return {slot, pendingTransactionId}
      } catch (e) {
        console.error('fetchMySlot failed:', e)
        return {slot: null, pendingTransactionId: null}
      }
    },
    // After a BIDV enroll-slot payment: reload the CV (enrollPayment) and the slot
    async refreshAfterEnrollPayment({commit, dispatch}, cvId) {
      try {
        const cv = await CV.fetchOne(cvId)
        if (cv) commit('setCV', cv)
      } catch (e) {
        console.error('Reload CV after enroll payment failed:', e)
      }
      await dispatch('fetchMySlot')
      alert.success(
        'Xác nhận đã đóng học phí thành công, PHHS vui lòng điền ĐƠN XIN NHẬP HỌC để hoàn thành việc Nhập học trực tuyến và được nhận Giấy hẹn Nộp hồ sơ gốc kèm mã nhập học',
      )
      commit('changeState', {isEnrollFormScrolled: true})
    },
    // Deprecated - ZALO_PAY: use fetchMySlot
    async checkEnrollPaymentStatus({commit, state}) {
      try {
        const slot = await EnrollSlot.checkEnrollPaymentStatus()
        commit('setSlot', slot)
        if (slot?.slot?.expectation) {
          commit('setSelectExpectation', slot?.slot?.expectation)
        }
        if (slot?.messageCode === 1) {
          commit('setCV', {...slot?.slot?.cv, enrollPayment: true})
          alert.success(
            'Xác nhận đã đóng học phí thành công, PHHS vui lòng điền ĐƠN XIN NHẬP HỌC để hoàn thành việc Nhập học trực tuyến và được nhận Giấy hẹn Nộp hồ sơ gốc kèm mã nhập học',
          )
          commit('changeState', {isEnrollFormScrolled: true})
        }
      } catch (e) {
        alert.error(e)
      }
    },
    async adminCheckEnrollPaymentStatus({commit, state}, data) {
      try {
        const slot = await EnrollSlot.adminCheckEnrollPaymentStatus(data)
        commit('setSlot', slot)
        if (slot?.slot?.expectation) {
          commit('setSelectExpectation', slot?.slot?.expectation)
        }
        if (slot?.messageCode === 1) {
          commit('setCV', {...slot?.slot?.cv, enrollPayment: true})
          alert.success(
            'Xác nhận đã đóng học phí thành công, PHHS vui lòng điền ĐƠN XIN NHẬP HỌC để hoàn thành việc Nhập học trực tuyến và được nhận Giấy hẹn Nộp hồ sơ gốc kèm mã nhập học',
          )
          commit('changeState', {isEnrollFormScrolled: true})
        }
        return slot
      } catch (e) {
        alert.error(e)
        return null
      }
    },
    async submitEnrollForm({commit, state}, payload) {
      const findSubmittedForm = async () => {
        if (!payload.studentCode) return null

        const forms = await EnrollForm.fetch({studentCode: payload.studentCode})
        return pickEnrollFormOfCV(forms, getCurrentCVFromState(state), payload.studentCode)
      }

      try {
        const submittedForm = await CV.submitDocument(payload)
        const result = submittedForm || (await findSubmittedForm())

        if (!result) {
          throw new Error('Không tìm thấy đơn nhập học sau khi nộp')
        }

        commit('setEnrollForm', result)
        alert.success('Xác nhận đã nộp đơn nhập học thành công')
        return result
      } catch (e) {
        // API có thể đã lưu đơn nhưng lỗi ở tác vụ sau lưu (ví dụ gửi thông báo).
        // Kiểm tra lại dữ liệu để giao diện không tiếp tục hiện nút nộp lần nữa.
        try {
          const submittedForm = await findSubmittedForm()
          if (submittedForm) {
            commit('setEnrollForm', submittedForm)
            alert.success('Xác nhận đã nộp đơn nhập học thành công')
            return submittedForm
          }
        } catch (verifyError) {
          // Giữ lỗi gốc để thông báo bên dưới có ý nghĩa hơn.
        }

        const message = typeof e === 'string' ? e : e?.message
        throw new Error(message || 'Nộp đơn nhập học thất bại!')
      }
    },
    async cancelEnrollSlot({commit}, data) {
      try {
        const result = await EnrollSlot.cancelEnrollSlot(data)
        commit('setCV', result)
        alert.success('Hủy đơn nhập học thành công')
      } catch (e) {
        alert.error(e)
      }
    },
    async cancelCV({commit}, data) {
      try {
        const result = await CV.cancelCV(data)
        commit('setCV', result)
        alert.success('Hủy hồ sơ thành công')
      } catch (e) {
        alert.error(e)
      }
    },
    async fetchEnrollForm({commit, state}, studentCode) {
      const result = await EnrollForm.fetch({
        studentCode: studentCode,
      })
      commit('setEnrollForm', pickEnrollFormOfCV(result, getCurrentCVFromState(state), studentCode))
      return result
    },
    async updateExpectation({commit}, data) {
      try {
        const slot = await EnrollSlot.updateExpectation(data)
        commit('setSlot', {slot: slot})
        alert.success('Cập nhật thành công!')
        return slot
      } catch (e) {
        alert.error(e)
        return null
      }
    },
    async adminUpdateExpectation({commit}, data) {
      try {
        const slot = await EnrollSlot.adminUpdateExpectation(data)
        commit('setSlot', {slot: slot})
        alert.success('Cập nhật thành công!')
        return slot
      } catch (e) {
        alert.error(e)
        return null
      }
    },
    async fetchEnrollSlot({commit}, options) {
      try {
        const slots = await EnrollSlot.fetch({...options})
        return slots
      } catch (e) {
        alert.error(e)
      }
    },
    async countEnrollSlot({commit}, options) {
      try {
        const total = await EnrollSlot.count({...options})
        return total
      } catch (error) {
        alert.error(error)
      }
    },
    async startHourlySMSTask() {
      try {
        await CV.startHourlySMSTask()
        alert.success('Start Hourly SMS task successfully!')
      } catch (e) {
        alert.error(e)
      }
    },
    async stopHourlySMSTask() {
      try {
        await CV.startHourlySMSTask()
        alert.success('Stop Hourly SMS task successfully!')
      } catch (e) {
        alert.error(e)
      }
    },
    async checkSystemTime({commit}) {
      try {
        const systemTime = await CV.checkSystemTime()
        commit('setSystemTime', systemTime)
        return systemTime
      } catch (e) {
        alert.error(e)
      }
    },
    async checkDocumentSystemTime({state}, {grade}) {
      try {
        return await CV.checkDocumentSystemTime({grade})
      } catch (e) {
        alert.error(e)
      }
    },
    async fetchCVs({commit}, options) {
      try {
        const cvs = await CV.fetch({...options})
        commit('setCVs', cvs)
        return cvs
      } catch (e) {
        alert.error(e)
      }
    },
    async filterSubjectExempt({commit}, options) {
      try {
        const cvs = await CV.filterSubjectExempt({...options})
        commit('setCVs', cvs)
        return cvs
      } catch (e) {
        alert.error(e)
      }
    },
    async getCvs({commit}, options) {
      try {
        const cvs = await CV.fetch({...options, _limit: -1})
        return cvs
      } catch (e) {
        alert.error(e)
      }
    },
    async countCVs({commit}, options) {
      try {
        return await CV.count(options)
      } catch (e) {
        alert.error(e)
      }
    },
    async countSubjectExempt({commit}, options) {
      try {
        return await CV.countSubjectExempt(options)
      } catch (e) {
        alert.error(e)
      }
    },
    async fetchCV({commit}, {...item}) {
      try {
        const cv = await CV.search({...item})
        if (cv.length > 0) {
          commit('setCV', cv[0])
          commit('setStep', cv[0].step)
          return cv[0]
        }
      } catch (e) {
        alert.error(e)
      }
    },
    async createCV({commit}, {parent, ...data}) {
      try {
        const newCV = await CV.create(parent, data)
        commit('setCV', newCV)
        alert.success('Tạo hồ sơ mới thành công!')
        return newCV
      } catch (e) {
        alert.error(e)
      }
    },
    async updateCV({commit}, {id, ...Cv}) {
      try {
        const updatedCV = await CV.update(id, {...Cv})
        commit('setCV', updatedCV)
        return updatedCV
      } catch (e) {
        alert.error(e)
      }
    },
    async removeCV({commit}, id) {
      try {
        const removedCV = await CV.remove(id)
        commit('removeCV', removedCV.code)
        alert.success('Xóa hồ sơ thành công!')
      } catch (e) {
        alert.error(e)
      }
    },
    async updateManyCheckAttributes({commit, state}, data) {
      try {
        await CV.updateManyCheckAttributes(data)
        const updatedCVs = Object.values(state.CVs).map(cv => {
          const findItem = data.items.find(item => item.id === cv.id)
          if (findItem) {
            return {
              ...cv,
              [data.attribute]: findItem.isChecked,
            }
          }
          return cv
        })
        commit('setCVs', updatedCVs)
        alert.success('Cập nhật thành công!')
      } catch (e) {
        alert.error(e)
      }
    },
    async confirmExamDate({commit}, data) {
      try {
        const result = await StudentExam.confirmExamDate(data)
        commit('setCV', result)
        alert.success('Xác nhận thành công!')
      } catch (e) {
        alert.error(e)
        throw e
      }
    },
    setCV({commit}, Cv) {
      commit('setCV', Cv)
    },
    setStep({commit, state}, step) {
      commit('setStep', step)
    },
    async removeCVs({dispatch}, items) {
      for (let item of items) {
        await dispatch('removeCV', item.code)
      }
    },
    setCVs({commit}, cvs) {
      commit('setCVs', cvs)
    },
    async exemptSubject({commit}, {id, status, subject}) {
      try {
        await CV.exemptSubject(id, {
          status: status,
          subject: subject,
        })
      } catch (e) {
        console.error(e)
        alert.error('Cập nhật thất bại')
      }
    },
    async approveAdmission({commit}, {id, data}) {
      try {
        await CV.approveAdmission(id, data)
      } catch (error) {
        console.log('error: ', error)
        throw error
      }
    },
  },
  mutations: {
    setIsAdminView(state, value) {
      state.isAdminView = value
    },
    setTempCVAttr(state, {type, value}) {
      state.tempCV = {
        ...state.tempCV,
        [type]: value,
      }
    },
    setAvatarChanged(state, value) {
      state.isAvatarChanged = value
    },
    setTempCV(state, value) {
      state.tempCV = value
    },
    setCVs(state, CVs) {
      state.CVs = CVs.reduce(
        (accumulator, currentValue) => ({
          ...accumulator,
          [`${currentValue.id}`]: currentValue,
        }),
        {},
      )
    },
    setCV(state, CV) {
      state.CVs = {
        ...state.CVs,
        [CV.id]: CV,
      }
    },
    setEnrollForm(state, enrollForm) {
      state.enrollForm = enrollForm
    },
    setSlot(state, slot) {
      state.slot = slot?.slot
    },
    setSelectExpectation(state, selectExpectation) {
      state.selectExpectation = selectExpectation
    },
    setStep(state, step) {
      state.step = step
    },
    setSystemTime(state, systemTime) {
      state.systemTime = systemTime
    },
    removeCV(state, id) {
      delete state.CVs[id]
      state.CVs = {...state.CVs}
    },
    setAttribute(state, {attribute, value}) {
      state[attribute] = value
    },
  },
  getters: {
    isEnrollFormScrolled: state => state.isEnrollFormScrolled,
    currentQuery: state => {
      return state.query
    },
    systemTime: state => {
      return state.systemTime
    },
    CVs: state => {
      return Object.values(state.CVs)
    },
    CV: state => id => {
      return state.CVs[id]
    },
    currentCV: state => {
      const result = state.isAdminView ? state.tempCV : Object.values(state.CVs)[0]
      return result
    },
    getTempCV: state => {
      return state.tempCV
    },
    count: state => {
      return state.count
    },
    step: state => {
      return state.step
    },
    getSelectedExpectation: state => {
      return state.selectExpectation
    },
    pendingEnrollTransactionId: state => state.pendingEnrollTransactionId,
    getSlot: state => {
      return state.slot
    },
    getEnrollForm: state => {
      return state.enrollForm
    },
    // Getter dùng chung cho các component sẽ được migrate dần.
    // Cách gọi: this.admissionProductStatus(productId).
    // Kết quả trả về: pass, passCond, fail hoặc null nếu product chưa có kết quả.
    admissionProductStatus: state => productId => {
      return selectAdmissionProductStatus(getCurrentCVFromState(state), productId)
    },
    highestScores: state => {
      const currentCv = getPrimaryCVFromState(state)

      // Giữ nguyên giá trị mặc định của business cũ khi học sinh chưa có kết quả xét tuyển.
      if (!currentCv || getAdmissionResults(currentCv).length === 0) {
        return {
          literature: 0,
          math: 0,
          english: 0,
          activityScore: 'fail',
          productStatuses: [],
        }
      }

      return {
        literature: getHighestScore(currentCv, 'literature'),
        math: getHighestScore(currentCv, 'math'),
        english: getHighestEnglishScore(currentCv),
        // Điểm hoạt động đã chuyển vào scores.activity; không đọc activityScore ở root theo schema cũ.
        activityScore: getAdmissionResults(currentCv).some(result => result?.scores?.activity === 'pass')
          ? 'pass'
          : 'fail',
        // Không tạo key riêng theo từng loại hệ. Khi BE thêm một product mới vào results[],
        // getter tự trả thêm phần tử tương ứng mà FE không cần bổ sung field *Admitted mới.
        productStatuses: selectAdmissionProductStatuses(currentCv),
      }
    },
    hasEnrollResultGrade10: state => {
      const currentCv = getPrimaryCVFromState(state)
      if (!currentCv || getAdmissionResults(currentCv).length === 0) {
        return false
      }
      const enrollmentMethods = Array.isArray(currentCv?.studyRecord?.enrollmentMethods)
        ? currentCv.studyRecord.enrollmentMethods
        : []
      const isOnlyEntranceExam =
        enrollmentMethods.includes('entrance_exam') && enrollmentMethods.length === 1

      // Giữ nguyên business cũ: hồ sơ chỉ đăng ký phương thức điểm thi vào 10 chưa được tính là có kết quả nhập học.
      if (isOnlyEntranceExam) return false

      // Business cũ cho phương thức tuyển thẳng trả true ngay khi đã có bản ghi kết quả.
      // Schema mới nhận diện phương thức bằng type/method.code, không dùng chuỗi checkpoint.
      const hasDirectAdmission = getAdmissionResults(currentCv).some(result => {
        return (
          DIRECT_ADMISSION_TYPES.includes(result?.type) ||
          DIRECT_ADMISSION_METHOD_CODES.includes(result?.method?.code)
        )
      })
      if (hasDirectAdmission) return true

      // Với các phương thức còn lại, results[] thay thế nhóm field *Admitted cũ.
      // Chỉ cần một bản ghi có kết quả gắn product.id là đã hoàn thành bước trả kết quả xét tuyển.
      return getAdmissionResults(currentCv).some(admission => {
        return Array.isArray(admission?.results) && admission.results.some(result => result?.product?.id)
      })
    },
    admissionExpectation: state => {
      const currentCv = getPrimaryCVFromState(state)
      if (!currentCv || !Array.isArray(currentCv.admissionResult)) {
        return {
          expectation1: {isAdmitted: false},
          expectation2: {isAdmitted: false},
        }
      }

      // Dùng cùng một biến đạt cho cả expectation1 và expectation2 như luồng hiện tại:
      // chỉ cần một trong các hệ thuộc nguyện vọng đạt hoặc đạt có điều kiện thì cả hai cờ isAdmitted đều bật.
      // Ở schema mới vẫn giữ đúng hành vi đó, nhưng tra cứu từng hệ bằng product.id.
      const enrollmentExpectations = [currentCv.expectation1, currentCv.expectation2].filter(Boolean)
      const isAnyExpectationAdmitted = enrollmentExpectations.some(expectation => {
        const status = selectAdmissionProductStatus(currentCv, expectation?.id)
        // Đạt có điều kiện vẫn là một kết quả đạt và được phép tiếp tục luồng nhập học.
        return status === 'pass' || status === 'passCond'
      })

      return {
        expectation1: {
          ...(currentCv.expectation1 || {}),
          isAdmitted: isAnyExpectationAdmitted,
        },
        expectation2: {
          ...(currentCv.expectation2 || {}),
          isAdmitted: isAnyExpectationAdmitted,
        },
      }
    },
    isEditableDocument: state => {
      let currentCV = Object.values(state.CVs)[0]
      if (!currentCV) return false
      const createdAt = moment(currentCV.createdAt)
      const startDate = moment('2026-03-03')
      const endDate = moment('2026-04-23').endOf('day')
      const now = moment()
      if (
        createdAt.isBetween(startDate, endDate, null, '[]') &&
        currentCV.documentSubmitStatus !== 'updated' &&
        currentCV.documentSubmitStatus !== 'submitted'
      )
        return true
      if (createdAt.isAfter(moment('2026-04-24'))) return true
      return createdAt.isBetween(startDate, endDate, null, '[]') && now.isBefore(moment('2026-04-24'))
    },
    isAvatarChanged: state => state.isAvatarChanged,
  },
}
/* eslint-enable no-unused-vars */
